import React from "react";

export const SANCTIONS_SOURCE_LINKS = {
  ofac: {
    page: "https://sanctionslist.ofac.treas.gov/Home/SdnList",
    file: "https://sanctionslistservice.ofac.treas.gov/api/PublicationPreview/exports/SDN.XML",
    format: "XML",
  },
  un: {
    page: "https://main.un.org/securitycouncil/en/content/un-sc-consolidated-list",
    file: "https://scsanctions.un.org/resources/xml/en/name/consolidated.xml",
    format: "XML",
  },
  uae: {
    page: "https://www.uaeiec.gov.ae/en-us/un-page?p=2",
    file: "https://www.uaeiec.gov.ae/API/Upload/DownloadFile?FileID=0433bfdb-8a3d-44db-9015-90cbbf48f6f6",
    format: "XLS",
  },
};

export function ofacImportStatus(meta) {
  const active = meta?.active || {};
  const files = Array.isArray(active.files) ? active.files : [];
  const nameOf = file => String(file?.fileName || '').toUpperCase();
  const primary = files.find(file => file?.storagePath === active.storagePath);
  const xml = primary && nameOf(primary).endsWith('.XML');
  // Check the manifest belongs to the active publication, not leftover metadata
  // merged into a later legacy upload. A download link alone proves no import.
  const manifestValid = !!primary && !!active.parserVersion && !!active.sha256 &&
    files.every(file => file?.storagePath && file?.sha256 && Number(file?.sizeBytes) > 0);
  const hasAliasCount = Number.isInteger(meta?.aliasCount) && meta.aliasCount > 0;
  const confirmed = meta?.parseStatus === 'ok' && !!meta.parsedStoragePath &&
    manifestValid && hasAliasCount && !!xml && files.length === 1;
  return {
    confirmed,
    files: manifestValid ? files : [],
    fileCount: manifestValid ? files.length : active.fileName ? 1 : 0,
    fileNames: manifestValid ? files.map(file => file.fileName).join(' + ') : active.fileName || '—',
    aliasCount: confirmed ? meta.aliasCount : null,
    note: confirmed
      ? 'Listings and aliases imported from the complete XML file'
      : 'Complete XML import not confirmed for this active version',
  };
}

export function SanctionsActiveFiles({ sourceKey, meta, formatBytes }) {
  const active = meta?.active || {};
  const ofac = sourceKey === 'ofac' ? ofacImportStatus(meta) : null;
  const fileNames = ofac?.files.length ? ofac.files.map(file => file.fileName) : [active.fileName || '—'];
  return (
    <div className="d-flex flex-column gap-1">
      {fileNames.map(fileName => {
        const aliasFile = ofac && fileName === 'ALT.CSV';
        const file = ofac?.files.find(item => item.fileName?.toUpperCase() === fileName.toUpperCase()) ||
          (active.fileName?.toUpperCase() === fileName.toUpperCase() ? active : null);
        const available = !!file && (!aliasFile || Number.isInteger(meta?.aliasCount));
        const count = aliasFile ? meta?.aliasCount : meta?.recordCount;
        return (
          <div key={fileName}>
            <div style={{ color: '#4C5465' }}>
              {fileName} · {available ? `${formatBytes(file.sizeBytes)} · ${typeof count === 'number' ? count.toLocaleString('en-GB') : '—'} records` : 'Not confirmed'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function importTime(meta) {
  return (meta?.active?.importMethod === 'automatic' && meta?.autoRefresh?.lastImportedAt) ||
    meta?.updatedAt || meta?.uploadedAt;
}

function timeValue(value) {
  if (value == null) return NaN;
  return (typeof value.toDate === 'function' ? value.toDate() : new Date(value)).getTime();
}

export function SanctionsSourceStatusBadge({ sourceKey, meta, now = Date.now() }) {
  const refresh = meta?.autoRefresh;
  const ofac = sourceKey === 'ofac' ? ofacImportStatus(meta) : null;
  let label = 'No active source', color = 'secondary';
  if (meta?.parseStatus === 'ok') {
    label = 'Imported'; color = 'success';
    if (refresh?.enabled) {
      // A previous successful check must not imply freshness indefinitely.
      const checked = timeValue(refresh.lastCheckedAt || importTime(meta));
      const recent = Number.isFinite(checked) && now - checked <= 36 * 60 * 60 * 1000;
      label = recent ? 'Up to date' : 'Check needed';
      color = recent ? 'success' : 'warning';
    }
    if (ofac && !ofac.confirmed) { label = 'Import incomplete'; color = 'warning'; }
  }
  if (refresh?.status === 'queued') { label = 'Update queued'; color = 'secondary'; }
  if (refresh?.status === 'running') { label = 'Checking…'; color = 'secondary'; }
  if (refresh?.status === 'error' || meta?.parseStatus === 'error') { label = 'Update failed'; color = 'warning'; }
  if (['uploading', 'pending'].includes(meta?.parseStatus)) { label = 'Importing…'; color = 'secondary'; }
  const incomplete = label === 'Import incomplete';
  return <span className={`badge${incomplete ? '' : ` text-bg-${color}`}`}
    style={incomplete ? { backgroundColor: '#D5B380', color: '#FFFFFF', fontWeight: 500 } : undefined}
    aria-live="polite">{label}</span>;
}

export function SanctionsActiveSource({ sourceKey, meta, formatBytes, formatDate }) {
  const ofac = sourceKey === 'ofac' ? ofacImportStatus(meta) : null;
  return (
    <div className="small" aria-live="polite">
      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
        <span style={{ fontWeight: 600, color: '#4C5465' }}>Active source</span>
      </div>
      <SanctionsActiveFiles sourceKey={sourceKey} meta={meta} formatBytes={formatBytes} />
      <SanctionsSourceRefreshInfo meta={meta} formatDate={formatDate} />
      {ofac && meta?.parseStatus === 'ok' && !ofac.confirmed && <div className="text-muted mt-1">{ofac.note}</div>}
    </div>
  );
}

export function SanctionsSourceAgeBadge({ meta, now = Date.now() }) {
  if (meta?.parseStatus !== 'ok') return null;
  // Successful import time, never lastCheckedAt: unchanged checks must not reset age.
  const value = (meta.active?.importMethod === 'automatic' && meta.autoRefresh?.lastImportedAt) ||
    meta.updatedAt || meta.uploadedAt;
  if (value == null) return null;
  const importedAt = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (!Number.isFinite(importedAt.getTime())) return null;
  const days = Math.max(0, Math.floor((now - importedAt.getTime()) / 86_400_000));
  return (
    <span className={`badge ${days > 3 ? 'text-bg-danger' : 'text-bg-success'}`}
      title={`Successfully imported ${importedAt.toLocaleString()}. Age in completed days.`}>
      J-{days}
    </span>
  );
}

export function SanctionsSourceReferences({ sourceKey, active, children }) {
  const source = SANCTIONS_SOURCE_LINKS[sourceKey];
  if (!source) return null;
  let file = source.file;
  // Only render an imported URL when it belongs to the expected publisher.
  try {
    const candidate = new URL(active?.sourceUrl);
    if (sourceKey !== "ofac" && candidate.protocol === "https:" && candidate.origin === new URL(source.file).origin &&
        !candidate.username && !candidate.password) file = candidate.href;
  } catch (_) {}
  return (
    <>
      <a className="btn btn-sm btn-outline-secondary" href={source.page} target="_blank" rel="noopener noreferrer">Official source</a>
      <a className="btn btn-sm btn-outline-secondary" href={file} target="_blank" rel="noopener noreferrer">Download file</a>
      {children}
    </>
  );
}

export default function SanctionsSourceRefreshInfo({ meta, formatDate }) {
  const refresh = meta?.autoRefresh;
  const imported = meta?.parseStatus === 'ok' ? importTime(meta) : null;
  const updatedLabel = Number.isFinite(timeValue(imported)) ? formatDate(imported).replace(' at ', ', ') : '';
  const checked = refresh?.lastCheckedAt;
  const checkedLabel = Number.isFinite(timeValue(checked)) ? formatDate(checked).replace(' at ', ', ') : '';
  // Timestamp writes for one import can differ by milliseconds. Compare the
  // displayed dates too so the same minute is not repeated as a separate check.
  const showCheck = checkedLabel && (!updatedLabel || (timeValue(checked) > timeValue(imported) && checkedLabel !== updatedLabel));
  const daily = refresh?.schedule?.match(/^(\d{2}:\d{2}) Asia\/Dubai daily$/);
  const schedule = daily ? `daily at ${daily[1]} Dubai` : refresh?.schedule || 'daily';
  const failed = refresh?.status === 'error' || meta?.parseStatus === 'error';
  return (
    <div className="text-muted mt-1">
      {(updatedLabel || refresh?.enabled) && <div>
        {updatedLabel && <>Updated {updatedLabel}</>}
        {updatedLabel && refresh?.enabled && ' · '}
        {refresh?.enabled && <>Auto-update {schedule}</>}
      </div>}
      {showCheck && <div>Last checked {checkedLabel}{refresh?.status === 'unchanged' && ' — no changes'}</div>}
      {failed && (
        <div className="mt-1" role="status">
          {meta?.parseStatus === 'ok' && meta?.active ? 'Previous version remains active.' : 'No new version was imported.'}
          {refresh?.retryAt && <> Retry {formatDate(refresh.retryAt)}.</>}
        </div>
      )}
    </div>
  );
}

export async function requestSanctionsSourceRefresh({ sourceKey, user, apiBase, fetchImpl = fetch }) {
  if (!['ofac', 'un', 'uae'].includes(sourceKey)) throw new Error('Unknown sanctions source.');
  if (!user) throw new Error('Sign in to update a source.');
  const token = await user.getIdToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetchImpl(`${apiBase}/myKyc/admin/sanctions-sources/${sourceKey}/refresh`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: '{}', signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));
    if (response.status !== 202 || !data.ok) throw new Error(data.error || 'Could not request the update. Check the source status and try again.');
    return data;
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('The request timed out. Check the source status before trying again.');
    throw error;
  } finally { clearTimeout(timer); }
}

export function SanctionsSourceUpdateButton({ title, meta, requesting, disabled, onUpdate }) {
  const status = meta?.autoRefresh?.status;
  const working = requesting || status === 'queued' || status === 'running';
  const manualBusy = ['uploading', 'pending'].includes(meta?.parseStatus);
  return (
    <button type="button" className="btn btn-sm btn-outline-secondary"
      disabled={disabled || working || manualBusy} onClick={onUpdate}
      aria-label={`Update ${title} from official source`} aria-busy={!!working}>
      {requesting ? 'Requesting…' : status === 'queued' ? 'Queued…' : status === 'running' ? 'Updating…' : 'Update now'}
    </button>
  );
}
